import React from 'react';
import { Template, TemplateListProps } from './types';

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  selectedTemplate,
  loading,
  error,
  filters,
  onSelectTemplate,
  onCreateNew,
  onFiltersChange,
  onLoadMore,
  hasMore
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ search: e.target.value });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ category: e.target.value });
  };

  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ difficulty: e.target.value });
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      general: '📝',
      consultation: '💬',
      discovery: '🔍',
      presentation: '📊',
      'follow-up': '📞',
      compliance: '✅'
    };
    return icons[category] || '📝';
  };

  const getDifficultyColor = (difficulty?: string) => {
    const colors: Record<string, string> = {
      beginner: 'bg-green-100 text-green-800',
      intermediate: 'bg-yellow-100 text-yellow-800',
      advanced: 'bg-red-100 text-red-800'
    };
    return colors[difficulty || 'beginner'] || 'bg-gray-100 text-gray-800';
  };

  const getDifficultyLabel = (difficulty?: string) => {
    const labels: Record<string, string> = {
      beginner: 'Standard',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    };
    return labels[difficulty || 'beginner'] || difficulty;
  };

  if (error) {
    return (
      <div className="flex flex-col h-full bg-white border-r border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center">
            <a
              href="/"
              className="p-1 mr-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Go to Homepage"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </a>
            <h2 className="text-lg font-semibold text-gray-900">Neural Templates</h2>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="text-red-500 text-sm mb-2">⚠️ Error</div>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <a
              href="/"
              className="p-1 mr-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Go to Homepage"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </a>
            <h2 className="text-lg font-semibold text-gray-900">Neural Templates</h2>
          </div>
          <button
            onClick={onCreateNew}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            title="Create New Template"
          >
            + New
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search templates..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-2">
          <select
            value={filters.category}
            onChange={handleCategoryChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="consultation">Consultation</option>
            <option value="discovery">Discovery</option>
            <option value="presentation">Presentation</option>
            <option value="follow-up">Follow-up</option>
            <option value="compliance">Compliance</option>
          </select>

          <select
            value={filters.difficulty}
            onChange={handleDifficultyChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">All Complexity</option>
            <option value="beginner">Standard</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
      </div>

      {/* Template List */}
      <div className="flex-1 overflow-y-auto">
        {loading && templates.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
            {filters.search || filters.category !== 'all' || filters.difficulty !== 'all' 
              ? 'No templates match your filters' 
              : 'No templates yet. Create your first one!'}
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className={`
                  p-3 rounded-lg cursor-pointer transition-colors border-2
                  ${selectedTemplate?.id === template.id
                    ? 'bg-blue-50 border-blue-200 shadow-sm'
                    : 'bg-white border-transparent hover:bg-gray-50'
                  }
                `}
              >
                {/* Row 1: Title and Complexity */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getCategoryIcon(template.category)}</span>
                    <h3 className="font-medium text-gray-900 text-sm truncate">
                      {template.name}
                    </h3>
                  </div>
                  {template.metadata.difficulty && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(template.metadata.difficulty)}`}>
                      {getDifficultyLabel(template.metadata.difficulty)}
                    </span>
                  )}
                </div>

                {/* Row 2: Description */}
                {template.description && (
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Row 3: All metadata in one row */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <span>{template.elements.length} elements</span>
                    {template.metadata.estimatedDuration && (
                      <span>⏱️ {template.metadata.estimatedDuration}</span>
                    )}
                    <span>{template.usageCount} uses</span>
                  </div>
                  <span>{formatDate(template.createdAt)}</span>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="p-2">
                <button
                  onClick={onLoadMore}
                  disabled={loading}
                  className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-400"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    'Load More Templates'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList; 